// DeleteConfirmGate — client-side delete confirmation text gate (ts-4).
//
// Mirrors the client-side gate to the server-side hard-delete guard.
//
// Markup convention:
//   Attach phx-hook="DeleteConfirmGate" to the confirm-delete wrapper div.
//   Set data-expected={@repo.full_path} on the wrapper.
//   The wrapper must contain:
//     - an <input name> for the typed confirmation text
//     - a <button data-confirm-btn> for the confirm action

import type { LiveViewHook } from "./types";

export interface DeleteConfirmGateState {
  _onInput: () => void;
  // The text the server was last told, or null before the first push.
  _lastPushed: string | null;
}

export const DeleteConfirmGate = {
  mounted(
    this: LiveViewHook<never, "update_delete_confirmation"> &
      DeleteConfirmGateState,
  ) {
    const input = this.el.querySelector<HTMLInputElement>("input[name]");
    const btn = this.el.querySelector<HTMLButtonElement>("[data-confirm-btn]");
    const expected = this.el.dataset["expected"] ?? "";
    this._lastPushed = null;

    if (!input || !btn) return;

    const update = (): void => {
      const match = input.value === expected;
      btn.disabled = !match;
      btn.classList.toggle("opacity-50", !match);
      btn.classList.toggle("cursor-not-allowed", !match);
      // Only a CHANGE goes to the server. Every push re-renders the
      // wrapper, which runs `updated()` and so this function again with
      // the same text: pushing unconditionally sent two messages per
      // keystroke, the second one carrying nothing new.
      if (input.value === this._lastPushed) return;
      this._lastPushed = input.value;
      this.pushEvent("update_delete_confirmation", { text: input.value });
    };

    input.addEventListener("input", update);
    this._onInput = update;
    update();
  },

  updated(
    this: LiveViewHook<never, "update_delete_confirmation"> &
      DeleteConfirmGateState,
  ) {
    // Re-run on LiveView update (e.g. form reset)
    this._onInput();
  },
};
